import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController, NavParams } from '@ionic/angular';
import { EnumPageRow } from '@models/Common';
import { EnumConsumersLoadStatus } from '@models/Consumers';
import { IExternalManualProcessEntry, IExternalManualProcessFilters, IExternalManualProcessSaveModel, EXTERNAL_MANUAL_PROCESS_STATUS_NAMES } from '@models/ExternalManualProcess';
import { EnumGenericRoleActionAlerts } from '@pages/BasePage';

export interface IExternalManualProcessHistoryResponse {
    Result?: IExternalManualProcessEntry[];
    TotalRegists?: number;
}

@Component({
    selector: 'app-external-manual-process',
    templateUrl: './external-manual-process.component.html',
    styleUrls: ['./external-manual-process.component.scss'],
})
export class ExternalManualProcessModalComponent implements OnInit {

    public static readonly OBSERVATION_MAX_LENGTH = 500;

    public readonly ObservationMaxLength = ExternalManualProcessModalComponent.OBSERVATION_MAX_LENGTH;

    public readonly ItemsPerPage: number[] = [EnumPageRow.R25, EnumPageRow.R50, EnumPageRow.R100];

    public readonly Statuses = Object.keys(EXTERNAL_MANUAL_PROCESS_STATUS_NAMES)
        .map(id => ({ Id: Number(id) as EnumConsumersLoadStatus, Name: EXTERNAL_MANUAL_PROCESS_STATUS_NAMES[Number(id)] }));

    public Title: string = 'Processo manual SIBS';

    public IsSubmitting: boolean = false;

    /** Mostra o controlo de paginação; desligado enquanto o endpoint devolver a lista completa. */
    public IsPaginated: boolean = false;

    public Form: FormGroup;

    public Data: IExternalManualProcessEntry[] = [];

    public TotalRegists: number = 0;

    public Filters = { PageNumber: 1, PageRows: EnumPageRow.R25 as number };

    public IsLoading: boolean = false;

    /** Callbacks da página que abriu o modal (recebidos em `componentProps`); o modal não fala com a API. */
    private readonly onLoadHistory: (filters: IExternalManualProcessFilters) => Promise<IExternalManualProcessHistoryResponse>;
    private readonly onChangePage: (ev: { page: number, itemsPerPage: number }) => Promise<IExternalManualProcessHistoryResponse>;
    private readonly onSave: (model: Omit<IExternalManualProcessSaveModel, 'IdMerchant'>) => Promise<{ IsSuccessStatus: boolean }>;

    constructor(
        private NavParams: NavParams,
        private ModalController: ModalController,
        private FormBuilder: FormBuilder) {

        this.Title = this.NavParams.get('title') || this.Title;
        this.IsPaginated = !!this.NavParams.get('paginated');
        this.onLoadHistory = this.NavParams.get('onLoadHistory');
        this.onChangePage = this.NavParams.get('onChangePage');
        this.onSave = this.NavParams.get('onSave');

        this.Form = this.FormBuilder.group({
            IdStatus: [null, Validators.required],
            Observation: ['', Validators.maxLength(this.ObservationMaxLength)]
        });
    }

    async ngOnInit() {
        await this.loadHistory();
    }

    public async OnSave() {

        if (this.IsSubmitting) return;

        if (this.Form.invalid) {
            this.Form.markAllAsTouched();
            return;
        }

        this.IsSubmitting = true;

        const { IdStatus, Observation } = this.Form.value;

        const response = await this.onSave({ IdStatus, Observation: (Observation || '').trim() });

        this.IsSubmitting = false;

        if (!response?.IsSuccessStatus) return;

        this.Form.reset({ IdStatus: null, Observation: '' });

        this.Filters.PageNumber = 1;

        await this.loadHistory();
    }

    public OnClear() {
        this.Form.reset({ IdStatus: null, Observation: '' });
    }

    public async OnChangePage(ev: { page: number, itemsPerPage: number }) {

        this.Filters.PageNumber = ev.page;
        this.Filters.PageRows = ev.itemsPerPage;

        this.IsLoading = true;

        this.setHistory(await this.onChangePage(ev));
    }

    public Close() {
        this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Close);
    }

    private async loadHistory() {

        this.IsLoading = true;

        this.setHistory(await this.onLoadHistory(this.Filters));
    }

    private setHistory(response: IExternalManualProcessHistoryResponse) {

        this.IsLoading = false;

        this.Data = response?.Result || [];
        this.TotalRegists = response?.TotalRegists || 0;
    }
}
